import { createContext, useContext, useState, useEffect } from "react";
import apiClient from "../api/apiClient";

const AppStateContext = createContext();

export function AppStateProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userId, setUserId] = useState();
    const [userProfile, setUserProfile] = useState();
    const [company, setCompany] = useState();
    const [given_name, setGivenName] = useState();
    const [sessionLoading, setSessionLoading] = useState(true);
    const [phone_number, setPhoneNumber] = useState();
    const [email, setEmail] = useState();
    const [profilePicUrl, setProfilePicUrl] = useState();
    const [familyName, setFamilyName] = useState();
    const [licenseNo, setLicenseNo] = useState([]);
    const [updated, setUpdated] = useState(false);
    const [headerAndMenu, setHeaderAndMenu] = useState(true);
    const [headerTitle, setHeaderTitle] = useState(""); 
    const [title, setTitle] = useState(""); 
    const [channelUrl, setChannelUrl] = useState(undefined);
    const [isContainerVisible, setIsContainerVisible] = useState(false);
    const [coaProcessing, setCOAProcessing] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
 
    useEffect(() => {
        localStorage.removeItem("coaProcessingJob");
    }, []);

    const applySessionData = (data) => {
        setUserId(data.sub);
        setGivenName(data.given_name);
        setPhoneNumber(data.phone_number);
        setEmail(data.email);
        setFamilyName(data.family_name);
        setUser(data);
        setTitle(data.title);
        setUserProfile(data.platform_role || "user");
        setHeaderAndMenu(true);
        setSessionExpired(false);
    };

    const clearSession = () => {
        localStorage.removeItem("tokens");
        setUser(null);
        setUserId(undefined);
        setUserProfile(undefined);
        setGivenName(undefined);
        setPhoneNumber(undefined);
        setEmail(undefined);
        setFamilyName(undefined);
        setTitle("");
        setHeaderAndMenu(false);
        setSessionExpired(false);
    };

    const expireSession = () => {
        localStorage.removeItem("tokens");
        setUser(null);
        setSessionExpired(true);
    };

    const completeLogin = async (tokens) => {
        if (!tokens?.AccessToken) return false;
        localStorage.setItem("tokens", JSON.stringify(tokens));
        setSessionLoading(true);
        try {
            const response = await apiClient.get("/user/validate-session", {
                headers: { accessToken: tokens.AccessToken },
            });
            if (response.data.valid) {
                applySessionData(response.data);
                return true;
            }
            setUser(null);
            return false;
        } catch {
            localStorage.removeItem("tokens");
            return false;
        } finally {
            setSessionLoading(false);
        }
    };

    useEffect(() => {
        const handler = () => expireSession();
        window.addEventListener("dyc:session-expired", handler);
        return () => window.removeEventListener("dyc:session-expired", handler);
    }, []);

    const tokensRaw = localStorage.getItem("tokens");
    useEffect(() => {
        const validateSession = async () => {
            const tokensString = localStorage.getItem("tokens");
            if (tokensString) {
                let tokens;
                try {
                    tokens = JSON.parse(tokensString);
                } catch {
                    console.error("Invalid tokens in localStorage, clearing.");
                    localStorage.removeItem("tokens");
                    setSessionLoading(false);
                    return;
                }
                if (tokens) {
                    try {
                        const response = await apiClient.get("/user/validate-session", {
                            headers: { accessToken: tokens.AccessToken },
                        });
                        if (response.data.valid) {
                            applySessionData(response.data);
                        } else {
                            setUser(null);
                        }
                    } catch (err) {
                        console.error(err);
                    }
                }
            }
            setSessionLoading(false);
        };
        validateSession();
    }, [tokensRaw]);

    const value = {
        user, setUser,
        userId, setUserId,
        userProfile, setUserProfile,
        company, setCompany,
        given_name, setGivenName,
        sessionLoading, setSessionLoading,
        phone_number, setPhoneNumber,
        email, setEmail,
        profilePicUrl, setProfilePicUrl,
        familyName, setFamilyName,
        licenseNo, setLicenseNo,
        updated, setUpdated,
        headerAndMenu, setHeaderAndMenu,
        headerTitle, setHeaderTitle,
        title, setTitle,
        channelUrl, setChannelUrl,
        isContainerVisible, setIsContainerVisible,
        coaProcessing, setCOAProcessing,
        sessionExpired,
        completeLogin,
    };

    return (
        <AppStateContext.Provider value={value}>
            {children}
        </AppStateContext.Provider>
    );
}

export function useAppState() {
    const ctx = useContext(AppStateContext);
    if (!ctx) throw new Error("useAppState must be used within AppStateProvider");
    return ctx;
}
